const API_BASE = (import.meta.env?.VITE_API_URL as string | undefined) || '/api';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  username: string;
  password: string;
  inviteCode?: string;
}

export interface AuthResponse {
  token: string;
  user: {
    id: string;
    email: string;
    username: string;
    role: 'admin' | 'user';
  };
}

export interface ApiError {
  message: string;
  errors?: Record<string, string[]>;
}

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('auth_token');
    }
    return this.token;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw data as ApiError;
    }

    return data as T;
  }

  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    this.setToken(response.token);
    return response;
  }

  async register(data: RegisterRequest): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    this.setToken(response.token);
    return response;
  }

  async logout(): Promise<void> {
    await this.request('/auth/logout', { method: 'POST' });
    this.setToken(null);
  }

  async getCurrentUser(): Promise<AuthResponse['user']> {
    return this.request('/auth/me');
  }

  // Tags API
  async getTags(): Promise<{ tags: Array<{ id: string; name: string; createdAt: Date }> }> {
    return this.request('/tags');
  }

  async createTag(name: string): Promise<{ tag: { id: string; name: string } }> {
    return this.request('/tags', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async deleteTag(tagId: string): Promise<{ success: boolean }> {
    return this.request(`/tags/${tagId}`, {
      method: 'DELETE',
    });
  }

  async addTagToUpload(tagId: string, uploadId: string): Promise<{ success: boolean }> {
    return this.request(`/tags/${tagId}/uploads/${uploadId}`, {
      method: 'POST',
    });
  }

  async removeTagFromUpload(tagId: string, uploadId: string): Promise<{ success: boolean }> {
    return this.request(`/tags/${tagId}/uploads/${uploadId}`, {
      method: 'DELETE',
    });
  }

  async getUploadsByTag(tagId: string): Promise<{ uploads: unknown[] }> {
    return this.request(`/tags/${tagId}/uploads`);
  }

  // Batch operations
  async batchDeleteUploads(
    uploadIds: string[]
  ): Promise<{ success: boolean; deletedCount: number }> {
    return this.request('/uploads/batch/delete', {
      method: 'POST',
      body: JSON.stringify({ uploadIds }),
    });
  }

  async batchAddTag(
    uploadIds: string[],
    tagId: string
  ): Promise<{ success: boolean; addedCount: number; skippedCount: number }> {
    return this.request('/uploads/batch/add-tag', {
      method: 'POST',
      body: JSON.stringify({ uploadIds, tagId }),
    });
  }

  async batchRemoveTag(uploadIds: string[], tagId: string): Promise<{ success: boolean }> {
    return this.request('/uploads/batch/remove-tag', {
      method: 'POST',
      body: JSON.stringify({ uploadIds, tagId }),
    });
  }

  async batchUpdatePrivacy(
    uploadIds: string[],
    isPublic: boolean
  ): Promise<{ success: boolean; updatedCount: number }> {
    return this.request('/uploads/batch/privacy', {
      method: 'POST',
      body: JSON.stringify({ uploadIds, isPublic }),
    });
  }

  // Uploads Management
  async getUploads(params?: {
    search?: string;
    type?: string;
    tag?: string;
    sort?: string;
  }): Promise<unknown[]> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.type) queryParams.set('type', params.type);
    if (params?.tag) queryParams.set('tag', params.tag);
    if (params?.sort) queryParams.set('sort', params.sort);

    const queryString = queryParams.toString();
    return this.request(`/uploads${queryString ? `?${queryString}` : ''}`);
  }

  async deleteUpload(id: string): Promise<{ message: string }> {
    return this.request(`/uploads/${id}`, { method: 'DELETE' });
  }

  async updateUpload(
    id: string,
    updates: { isPublic?: boolean; vanityUrl?: string }
  ): Promise<unknown> {
    return this.request(`/uploads/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  // Users Management
  async getUsers(params?: { search?: string; role?: string; status?: string }): Promise<unknown[]> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.role) queryParams.set('role', params.role);
    if (params?.status) queryParams.set('status', params.status);

    const queryString = queryParams.toString();
    return this.request(`/users${queryString ? `?${queryString}` : ''}`);
  }

  async createUser(data: {
    email: string;
    username: string;
    role?: 'admin' | 'user';
    diskQuota?: number;
    sendInvite?: boolean;
  }): Promise<{
    user: { id: string; email: string; username: string; role: string };
    inviteCode: string;
    tempPassword: string;
    emailSent: boolean;
    emailError: string | null;
  }> {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getUser(id: string): Promise<unknown> {
    return this.request(`/users/${id}`);
  }

  async updateUser(id: string, updates: unknown): Promise<unknown> {
    return this.request(`/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteUser(id: string): Promise<{ message: string }> {
    return this.request(`/users/${id}`, { method: 'DELETE' });
  }

  async resetUserPassword(id: string): Promise<{ message: string; tempPassword: string }> {
    return this.request(`/users/${id}/reset-password`, { method: 'POST' });
  }

  async changePassword(
    id: string,
    data: { currentPassword: string; newPassword: string }
  ): Promise<{ message: string }> {
    return this.request(`/users/${id}/change-password`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async regenerateApiToken(id: string): Promise<{ message: string; apiToken: string }> {
    return this.request(`/users/${id}/regenerate-token`, { method: 'POST' });
  }

  // Preferences
  async getPreferences(): Promise<{ preferences: unknown }> {
    return this.request('/preferences');
  }

  async updatePreferences(updates: unknown): Promise<{ preferences: unknown }> {
    return this.request('/preferences', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  // Statistics
  async getUserStats(): Promise<unknown> {
    return this.request('/stats');
  }

  async getAdminStats(): Promise<unknown> {
    return this.request('/stats/admin');
  }

  // Access Requests
  async submitAccessRequest(data: {
    email: string;
    username: string;
    reason?: string;
  }): Promise<{ message: string; id: string }> {
    return this.request('/access-requests', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getAccessRequests(): Promise<unknown[]> {
    return this.request('/access-requests');
  }

  async updateAccessRequest(
    id: string,
    data: { status: 'approved' | 'rejected' }
  ): Promise<unknown> {
    return this.request(`/access-requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteAccessRequest(id: string): Promise<{ message: string }> {
    return this.request(`/access-requests/${id}`, { method: 'DELETE' });
  }

  // Uploader Config Downloads (returns Blob for file downloads)
  async downloadFile(endpoint: string, filename: string): Promise<void> {
    const token = this.getToken();
    const response = await fetch(`${API_BASE}${endpoint}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) {
      throw new Error('Failed to download file');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  async downloadShareXConfig(username: string): Promise<void> {
    return this.downloadFile('/upload/sharex-config', `h0udini-${username}.sxcu`);
  }

  async downloadBashScript(): Promise<void> {
    return this.downloadFile('/upload/bash-script', 'h0udini-uploader.sh');
  }

  async downloadPowerShellScript(): Promise<void> {
    return this.downloadFile('/upload/powershell-script', 'h0udini-uploader.ps1');
  }

  async downloadUserShareXConfig(userId: string, username: string): Promise<void> {
    return this.downloadFile(`/users/${userId}/sharex-config`, `h0udini-${username}.sxcu`);
  }

  async downloadUserBashScript(userId: string): Promise<void> {
    return this.downloadFile(`/users/${userId}/bash-script`, 'h0udini-uploader.sh');
  }

  async downloadUserPowerShellScript(userId: string): Promise<void> {
    return this.downloadFile(`/users/${userId}/powershell-script`, 'h0udini-uploader.ps1');
  }

  // Email
  async sendInviteEmail(data: {
    to: string;
    username: string;
    tempPassword: string;
    inviteCode: string;
  }): Promise<{ success: boolean; data?: unknown; error?: string }> {
    try {
      const result = await this.request('/email/send-invite', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return { success: true, data: result };
    } catch (error) {
      return { success: false, error: (error as ApiError).message };
    }
  }
}

export const api = new ApiClient();
